/**
 * What colour an element is, expressed as a role rather than a value.
 *
 * Roles resolve through the theme, so switching a brand style preset restyles
 * every element — a literal hex baked into props would freeze the page in the
 * palette it was generated with, and regeneration cannot fix a page the owner
 * has edited.
 *
 * `auto` is the default and resolves from the frame the element sits in (see
 * `frameOnMediaClass`): light over photography, page colours everywhere else.
 * That is what lets a generator drop primitives into any frame and have them
 * come out legible without setting anything.
 */
export type ColorRole = "auto" | "foreground" | "muted" | "accent" | "onMedia";

export const COLOR_ROLE_OPTIONS = [
  { label: "Auto", value: "auto" },
  { label: "Default", value: "foreground" },
  { label: "Muted", value: "muted" },
  { label: "Accent", value: "accent" },
  { label: "On photo", value: "onMedia" },
] as const;

/**
 * Class for a role. `emphasis` picks which `auto` resolves to — a heading is a
 * page's strong text, body copy is its quiet text, and the frame publishes a
 * value for each.
 */
export function colorRoleClass(
  role: ColorRole | undefined,
  emphasis: "strong" | "quiet" = "strong",
): string {
  switch (role) {
    case "foreground":
      return "jobsite-color-foreground";
    case "muted":
      return "jobsite-color-muted";
    case "accent":
      return "jobsite-color-accent";
    case "onMedia":
      return "jobsite-color-on-media";
    case "auto":
    default:
      return emphasis === "quiet" ? "jobsite-color-auto-muted" : "jobsite-color-auto";
  }
}

/**
 * Marks a frame as sitting over photography, so `auto` children resolve light.
 * Applied by the frame, never by the element.
 */
export const FRAME_ON_MEDIA_CLASS = "jobsite-frame-on-media";
